import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Eye, Activity, Sparkles, AlertCircle, CircleDot } from "lucide-react";

export interface IridologyObservationsData {
  // Éléments remarquables
  elements_remarquables: string;
  
  // Premières impressions
  aspect_general: 'harmonique' | 'dysharmonique' | null;
  couleur: 'franche' | 'heterogene' | null;
  trame: 'compacte' | 'clairsemee' | null;
  pupilles: 'rondes' | 'deformees' | null;
  
  // Autres observations
  anisocorie: boolean;
  heterochromie: boolean;
  strabisme: boolean;
  debut_cataracte: boolean;
  autre_observation: string;
  
  // Défaut de vision
  myopie: boolean;
  hypermetropie: boolean;
  astigmatisme: boolean;
  presbytie: boolean;
  autre_defaut_vision: string;
  
  // Correction
  lunettes_vue: boolean;
  lunettes_vue_quand: string;
  lentilles: boolean;
  lunettes_repos: boolean;
  
  // Constitution principale
  constitution: 'lymphatique' | 'hematogene' | 'mixte_hepatique' | 'mixte_pancreatique' | null;
  sous_constitution: 'hydrogenoide' | 'neurogene' | 'glandulaire' | null;
  
  // Trame - échelle 1-5
  trame_od: number | null;
  trame_og: number | null;
  
  // Relief irien
  relief_od: 'canadienne' | 'bedouine' | 'igloo' | 'belle_etoile' | null;
  relief_og: 'canadienne' | 'bedouine' | 'igloo' | 'belle_etoile' | null;
  
  // Cercle pupillaire
  cercle_pupillaire_visible: string;
  parasympathique: 'ok' | 'hyper' | 'hypo' | null;
  orthosympathique: 'ok' | 'hyper' | 'hypo' | null;
  
  // Pupille droite
  pupille_d_anisocorie: boolean;
  pupille_d_diametre: 'mydriase' | 'ok' | 'myosis' | null;
  pupille_d_bords_grignotes: boolean;
  pupille_d_bords_cabosses: boolean;
  pupille_d_meplats: string;
  pupille_d_ovalisation: 'plus' | 'plus_plus' | 'plus_plus_plus' | null;
  pupille_d_ovalisation_secteurs: string;
  pupille_d_decentrement: 'plus' | 'plus_plus' | 'plus_plus_plus' | null;
  pupille_d_decentrement_secteurs: string;
  
  // Pupille gauche
  pupille_g_diametre: 'mydriase' | 'ok' | 'myosis' | null;
  pupille_g_bords_grignotes: boolean;
  pupille_g_bords_cabosses: boolean;
  pupille_g_meplats: string;
  pupille_g_ovalisation: 'plus' | 'plus_plus' | 'plus_plus_plus' | null;
  pupille_g_ovalisation_secteurs: string;
  pupille_g_decentrement: 'plus' | 'plus_plus' | 'plus_plus_plus' | null;
  pupille_g_decentrement_secteurs: string;
  
  // Intoxination
  acide_oxalique: boolean;
  acide_pyruvique: boolean;
  acide_urique: boolean;
  anneau_electrolytique: boolean;
  anneau_lipidique: boolean;
  anneau_gerontoxon: boolean;
  taches_psores: boolean;
  taches_cholesterol: boolean;
  
  // Taches résiduelles
  taches_cicatrisation: boolean;
  taches_stress: boolean;
  taches_hemorragique: boolean;
  
  // Signes iriens
  radii_solari: boolean;
  arcs_cercle: boolean;
  vacuoles: boolean;
  lacunes: boolean;
  cryptes: boolean;
  
  // Conclusions
  conclusions_psy: string;
}

export const defaultObservationsData: IridologyObservationsData = {
  elements_remarquables: '',
  aspect_general: null,
  couleur: null,
  trame: null,
  pupilles: null,
  anisocorie: false,
  heterochromie: false,
  strabisme: false,
  debut_cataracte: false,
  autre_observation: '',
  myopie: false,
  hypermetropie: false,
  astigmatisme: false,
  presbytie: false,
  autre_defaut_vision: '',
  lunettes_vue: false,
  lunettes_vue_quand: '',
  lentilles: false,
  lunettes_repos: false,
  constitution: null,
  sous_constitution: null,
  trame_od: null,
  trame_og: null,
  relief_od: null,
  relief_og: null,
  cercle_pupillaire_visible: '',
  parasympathique: null,
  orthosympathique: null,
  pupille_d_anisocorie: false,
  pupille_d_diametre: null,
  pupille_d_bords_grignotes: false,
  pupille_d_bords_cabosses: false,
  pupille_d_meplats: '',
  pupille_d_ovalisation: null,
  pupille_d_ovalisation_secteurs: '',
  pupille_d_decentrement: null,
  pupille_d_decentrement_secteurs: '',
  pupille_g_diametre: null,
  pupille_g_bords_grignotes: false,
  pupille_g_bords_cabosses: false,
  pupille_g_meplats: '',
  pupille_g_ovalisation: null,
  pupille_g_ovalisation_secteurs: '',
  pupille_g_decentrement: null,
  pupille_g_decentrement_secteurs: '',
  acide_oxalique: false,
  acide_pyruvique: false,
  acide_urique: false,
  anneau_electrolytique: false,
  anneau_lipidique: false,
  anneau_gerontoxon: false,
  taches_psores: false,
  taches_cholesterol: false,
  taches_cicatrisation: false,
  taches_stress: false,
  taches_hemorragique: false,
  radii_solari: false,
  arcs_cercle: false,
  vacuoles: false,
  lacunes: false,
  cryptes: false,
  conclusions_psy: '',
};

interface IridologyObservationsFormProps {
  data: IridologyObservationsData;
  onChange: (data: Partial<IridologyObservationsData>) => void;
  readOnly?: boolean;
}

export const IridologyObservationsForm: React.FC<IridologyObservationsFormProps> = ({
  data,
  onChange,
  readOnly = false,
}) => {
  const handleCheckboxChange = (field: keyof IridologyObservationsData, value: boolean) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Éléments remarquables */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary" />
            Éléments remarquables
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data.elements_remarquables}
            onChange={(e) => onChange({ elements_remarquables: e.target.value })}
            placeholder="Paupières, cernes, sclère..."
            disabled={readOnly}
            rows={2}
          />
        </CardContent>
      </Card>

      {/* Premières impressions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Premières impressions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Aspect général */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Aspect général</Label>
            <RadioGroup
              value={data.aspect_general || ''}
              onValueChange={(value) => onChange({ aspect_general: value as any })}
              disabled={readOnly}
              className="flex flex-wrap gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="harmonique" id="aspect-harmonique" />
                <Label htmlFor="aspect-harmonique" className="cursor-pointer text-sm text-green-600">Harmonique</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="dysharmonique" id="aspect-dysharmonique" />
                <Label htmlFor="aspect-dysharmonique" className="cursor-pointer text-sm text-orange-600">Dysharmonique</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Couleur */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Couleur</Label>
            <RadioGroup
              value={data.couleur || ''}
              onValueChange={(value) => onChange({ couleur: value as any })}
              disabled={readOnly}
              className="flex flex-wrap gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="franche" id="couleur-franche" />
                <Label htmlFor="couleur-franche" className="cursor-pointer text-sm">Franche, uniforme, dense</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="heterogene" id="couleur-heterogene" />
                <Label htmlFor="couleur-heterogene" className="cursor-pointer text-sm text-orange-600">
                  Hétérogène, tâchée → détoxiner
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Trame */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Trame</Label>
            <RadioGroup
              value={data.trame || ''}
              onValueChange={(value) => onChange({ trame: value as any })}
              disabled={readOnly}
              className="flex flex-wrap gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="compacte" id="trame-compacte" />
                <Label htmlFor="trame-compacte" className="cursor-pointer text-sm">Compacte, serrée, solide</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="clairsemee" id="trame-clairsemee" />
                <Label htmlFor="trame-clairsemee" className="cursor-pointer text-sm text-orange-600">
                  Clairsemée, effilochée → revitaliser
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Pupilles */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Pupilles</Label>
            <RadioGroup
              value={data.pupilles || ''}
              onValueChange={(value) => onChange({ pupilles: value as any })}
              disabled={readOnly}
              className="flex flex-wrap gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="rondes" id="pupilles-rondes" />
                <Label htmlFor="pupilles-rondes" className="cursor-pointer text-sm">Rondes, nettes, centrées</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="deformees" id="pupilles-deformees" />
                <Label htmlFor="pupilles-deformees" className="cursor-pointer text-sm text-red-600">
                  Déformées, grignotées → revitaliser +++
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Autres observations */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Autre</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: 'anisocorie', label: 'Anisocorie' },
                { key: 'heterochromie', label: 'Hétérochromie' },
                { key: 'strabisme', label: 'Strabisme' },
                { key: 'debut_cataracte', label: 'Début cataracte' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={data[key as keyof IridologyObservationsData] as boolean}
                    onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyObservationsData, !!checked)}
                    disabled={readOnly}
                  />
                  <Label htmlFor={key} className="text-sm cursor-pointer">{label}</Label>
                </div>
              ))}
            </div>
            <Input
              value={data.autre_observation}
              onChange={(e) => onChange({ autre_observation: e.target.value })}
              placeholder="Autre observation..."
              disabled={readOnly}
              className="mt-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Défaut de vision */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Défaut de vision</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'myopie', label: 'Myopie' },
              { key: 'hypermetropie', label: 'Hypermétropie' },
              { key: 'astigmatisme', label: 'Astigmatisme' },
              { key: 'presbytie', label: 'Presbytie' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2">
                <Checkbox
                  id={key}
                  checked={data[key as keyof IridologyObservationsData] as boolean}
                  onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyObservationsData, !!checked)}
                  disabled={readOnly}
                />
                <Label htmlFor={key} className="text-sm cursor-pointer">{label}</Label>
              </div>
            ))}
          </div>
          <Input
            value={data.autre_defaut_vision}
            onChange={(e) => onChange({ autre_defaut_vision: e.target.value })}
            placeholder="Autre défaut de vision..."
            disabled={readOnly}
          />
        </CardContent>
      </Card>

      {/* Correction */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Correction</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="lunettes_vue"
                  checked={data.lunettes_vue}
                  onCheckedChange={(checked) => handleCheckboxChange('lunettes_vue', !!checked)}
                  disabled={readOnly}
                />
                <Label htmlFor="lunettes_vue" className="text-sm cursor-pointer">Lunettes de vue</Label>
              </div>
              {data.lunettes_vue && (
                <Input
                  value={data.lunettes_vue_quand}
                  onChange={(e) => onChange({ lunettes_vue_quand: e.target.value })}
                  placeholder="Quand ?"
                  disabled={readOnly}
                  className="ml-6"
                />
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="lentilles"
                checked={data.lentilles}
                onCheckedChange={(checked) => handleCheckboxChange('lentilles', !!checked)}
                disabled={readOnly}
              />
              <Label htmlFor="lentilles" className="text-sm cursor-pointer">Lentilles</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="lunettes_repos"
                checked={data.lunettes_repos}
                onCheckedChange={(checked) => handleCheckboxChange('lunettes_repos', !!checked)}
                disabled={readOnly}
              />
              <Label htmlFor="lunettes_repos" className="text-sm cursor-pointer">Lunettes de repos</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Constitution principale */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-500" />
            Constitution principale / Sous-constitution
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Constitution</Label>
            <RadioGroup
              value={data.constitution || ''}
              onValueChange={(value) => onChange({ constitution: value as any })}
              disabled={readOnly}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              <div className="flex items-start space-x-2 p-2 rounded-lg border">
                <RadioGroupItem value="lymphatique" id="const-lymphatique" className="mt-1" />
                <div>
                  <Label htmlFor="const-lymphatique" className="cursor-pointer font-medium">Lymphatique fibrillaire</Label>
                  <p className="text-xs text-muted-foreground">Bleu/bleu-gris net</p>
                </div>
              </div>
              <div className="flex items-start space-x-2 p-2 rounded-lg border">
                <RadioGroupItem value="hematogene" id="const-hematogene" className="mt-1" />
                <div>
                  <Label htmlFor="const-hematogene" className="cursor-pointer font-medium">Hématogène</Label>
                  <p className="text-xs text-muted-foreground">Brun/marron foncé</p>
                </div>
              </div>
              <div className="flex items-start space-x-2 p-2 rounded-lg border">
                <RadioGroupItem value="mixte_hepatique" id="const-mixte-hep" className="mt-1" />
                <div>
                  <Label htmlFor="const-mixte-hep" className="cursor-pointer font-medium">Mixte hépatique</Label>
                  <p className="text-xs text-muted-foreground">Noisette/kaki/vert homogène</p>
                </div>
              </div>
              <div className="flex items-start space-x-2 p-2 rounded-lg border">
                <RadioGroupItem value="mixte_pancreatique" id="const-mixte-pan" className="mt-1" />
                <div>
                  <Label htmlFor="const-mixte-pan" className="cursor-pointer font-medium">Mixte pancréatique</Label>
                  <p className="text-xs text-muted-foreground">Zone brun-orangé "tournesol" autour pupille</p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Sous-constitution</Label>
            <RadioGroup
              value={data.sous_constitution || ''}
              onValueChange={(value) => onChange({ sous_constitution: value as any })}
              disabled={readOnly}
              className="grid grid-cols-1 sm:grid-cols-3 gap-2"
            >
              <div className="flex items-start space-x-2 p-2 rounded-lg border">
                <RadioGroupItem value="hydrogenoide" id="sous-hydro" className="mt-1" />
                <div>
                  <Label htmlFor="sous-hydro" className="cursor-pointer font-medium">Hydrogénoïde</Label>
                  <p className="text-xs text-muted-foreground">Bleu très clair, flocons blancs</p>
                </div>
              </div>
              <div className="flex items-start space-x-2 p-2 rounded-lg border">
                <RadioGroupItem value="neurogene" id="sous-neuro" className="mt-1" />
                <div>
                  <Label htmlFor="sous-neuro" className="cursor-pointer font-medium">Neurogène</Label>
                  <p className="text-xs text-muted-foreground">Radii solari, radiaires "tendus"</p>
                </div>
              </div>
              <div className="flex items-start space-x-2 p-2 rounded-lg border">
                <RadioGroupItem value="glandulaire" id="sous-gland" className="mt-1" />
                <div>
                  <Label htmlFor="sous-gland" className="cursor-pointer font-medium">Glandulaire</Label>
                  <p className="text-xs text-muted-foreground">Trame relâchée, vacuoles zone C4</p>
                </div>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {/* Trame - échelle 1-5 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Trame (échelle 1 à 5)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm">Œil Droit (OD)</Label>
              <RadioGroup
                value={data.trame_od?.toString() || ''}
                onValueChange={(value) => onChange({ trame_od: parseFloat(value) })}
                disabled={readOnly}
                className="flex flex-wrap gap-2"
              >
                {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((v) => (
                  <div key={v} className="flex items-center space-x-1">
                    <RadioGroupItem value={v.toString()} id={`trame-od-${v}`} />
                    <Label htmlFor={`trame-od-${v}`} className="text-sm cursor-pointer">{v}</Label>
                  </div>
                ))}
              </RadioGroup>
              <p className="text-xs text-muted-foreground">1-2: Fine/serrée | 2-3: Normale | 3-4: Lâche | 4-5: Lacunaire</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Œil Gauche (OG)</Label>
              <RadioGroup
                value={data.trame_og?.toString() || ''}
                onValueChange={(value) => onChange({ trame_og: parseFloat(value) })}
                disabled={readOnly}
                className="flex flex-wrap gap-2"
              >
                {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((v) => (
                  <div key={v} className="flex items-center space-x-1">
                    <RadioGroupItem value={v.toString()} id={`trame-og-${v}`} />
                    <Label htmlFor={`trame-og-${v}`} className="text-sm cursor-pointer">{v}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Relief irien */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Relief irien</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm">Œil Droit (OD)</Label>
              <RadioGroup
                value={data.relief_od || ''}
                onValueChange={(value) => onChange({ relief_od: value as any })}
                disabled={readOnly}
                className="grid grid-cols-2 gap-2"
              >
                {[
                  { value: 'canadienne', label: 'Canadienne' },
                  { value: 'bedouine', label: 'Bédouine' },
                  { value: 'igloo', label: 'Igloo' },
                  { value: 'belle_etoile', label: 'Belle-étoile' },
                ].map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`relief-od-${value}`} />
                    <Label htmlFor={`relief-od-${value}`} className="text-sm cursor-pointer">{label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Œil Gauche (OG)</Label>
              <RadioGroup
                value={data.relief_og || ''}
                onValueChange={(value) => onChange({ relief_og: value as any })}
                disabled={readOnly}
                className="grid grid-cols-2 gap-2"
              >
                {[
                  { value: 'canadienne', label: 'Canadienne' },
                  { value: 'bedouine', label: 'Bédouine' },
                  { value: 'igloo', label: 'Igloo' },
                  { value: 'belle_etoile', label: 'Belle-étoile' },
                ].map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`relief-og-${value}`} />
                    <Label htmlFor={`relief-og-${value}`} className="text-sm cursor-pointer">{label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cercle pupillaire et système nerveux autonome */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CircleDot className="w-5 h-5 text-purple-500" />
            Cercle pupillaire
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={data.cercle_pupillaire_visible}
            onChange={(e) => onChange({ cercle_pupillaire_visible: e.target.value })}
            placeholder="Visible, peu visible, très marqué..."
            disabled={readOnly}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Parasympathique</Label>
              <RadioGroup
                value={data.parasympathique || ''}
                onValueChange={(value) => onChange({ parasympathique: value as any })}
                disabled={readOnly}
                className="flex gap-4"
              >
                {[
                  { value: 'ok', label: 'Ok', color: 'text-green-600' },
                  { value: 'hyper', label: 'Hyper', color: 'text-red-600' },
                  { value: 'hypo', label: 'Hypo', color: 'text-blue-600' },
                ].map(({ value, label, color }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`para-${value}`} />
                    <Label htmlFor={`para-${value}`} className={`text-sm cursor-pointer ${color}`}>{label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Orthosympathique</Label>
              <RadioGroup
                value={data.orthosympathique || ''}
                onValueChange={(value) => onChange({ orthosympathique: value as any })}
                disabled={readOnly}
                className="flex gap-4"
              >
                {[
                  { value: 'ok', label: 'Ok', color: 'text-green-600' },
                  { value: 'hyper', label: 'Hyper', color: 'text-red-600' },
                  { value: 'hypo', label: 'Hypo', color: 'text-blue-600' },
                ].map(({ value, label, color }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`ortho-${value}`} />
                    <Label htmlFor={`ortho-${value}`} className={`text-sm cursor-pointer ${color}`}>{label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pupille - Analyse détaillée */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Pupille - Analyse détaillée</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center space-x-2 mb-4">
            <Checkbox
              id="pupille_d_anisocorie"
              checked={data.pupille_d_anisocorie}
              onCheckedChange={(checked) => handleCheckboxChange('pupille_d_anisocorie', !!checked)}
              disabled={readOnly}
            />
            <Label htmlFor="pupille_d_anisocorie" className="text-sm cursor-pointer font-medium">Anisocorie</Label>
          </div>

          {/* Œil droit */}
          <div className="p-4 border rounded-lg space-y-4">
            <h4 className="font-medium text-primary">Œil Droit</h4>
            
            <div className="space-y-2">
              <Label className="text-sm">Diamètre</Label>
              <RadioGroup
                value={data.pupille_d_diametre || ''}
                onValueChange={(value) => onChange({ pupille_d_diametre: value as any })}
                disabled={readOnly}
                className="flex gap-4"
              >
                {[
                  { value: 'mydriase', label: 'Mydriase' },
                  { value: 'ok', label: 'Ok' },
                  { value: 'myosis', label: 'Myosis' },
                ].map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`diam-d-${value}`} />
                    <Label htmlFor={`diam-d-${value}`} className="text-sm cursor-pointer">{label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Bords</Label>
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pupille_d_bords_grignotes"
                    checked={data.pupille_d_bords_grignotes}
                    onCheckedChange={(checked) => handleCheckboxChange('pupille_d_bords_grignotes', !!checked)}
                    disabled={readOnly}
                  />
                  <Label htmlFor="pupille_d_bords_grignotes" className="text-sm cursor-pointer">Grignotés</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pupille_d_bords_cabosses"
                    checked={data.pupille_d_bords_cabosses}
                    onCheckedChange={(checked) => handleCheckboxChange('pupille_d_bords_cabosses', !!checked)}
                    disabled={readOnly}
                  />
                  <Label htmlFor="pupille_d_bords_cabosses" className="text-sm cursor-pointer">Cabossés</Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Méplats (secteurs 1-12)</Label>
              <Input
                value={data.pupille_d_meplats}
                onChange={(e) => onChange({ pupille_d_meplats: e.target.value })}
                placeholder="Ex: 3, 7, 11"
                disabled={readOnly}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Ovalisation</Label>
                <RadioGroup
                  value={data.pupille_d_ovalisation || ''}
                  onValueChange={(value) => onChange({ pupille_d_ovalisation: value as any })}
                  disabled={readOnly}
                  className="flex gap-3"
                >
                  {['plus', 'plus_plus', 'plus_plus_plus'].map((v) => (
                    <div key={v} className="flex items-center space-x-1">
                      <RadioGroupItem value={v} id={`oval-d-${v}`} />
                      <Label htmlFor={`oval-d-${v}`} className="text-sm cursor-pointer">
                        {v === 'plus' ? '+' : v === 'plus_plus' ? '++' : '+++'}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                <Input
                  value={data.pupille_d_ovalisation_secteurs}
                  onChange={(e) => onChange({ pupille_d_ovalisation_secteurs: e.target.value })}
                  placeholder="Secteurs (1/7, 2/8, 6/12)"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Décentrement</Label>
                <RadioGroup
                  value={data.pupille_d_decentrement || ''}
                  onValueChange={(value) => onChange({ pupille_d_decentrement: value as any })}
                  disabled={readOnly}
                  className="flex gap-3"
                >
                  {['plus', 'plus_plus', 'plus_plus_plus'].map((v) => (
                    <div key={v} className="flex items-center space-x-1">
                      <RadioGroupItem value={v} id={`dec-d-${v}`} />
                      <Label htmlFor={`dec-d-${v}`} className="text-sm cursor-pointer">
                        {v === 'plus' ? '+' : v === 'plus_plus' ? '++' : '+++'}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                <Input
                  value={data.pupille_d_decentrement_secteurs}
                  onChange={(e) => onChange({ pupille_d_decentrement_secteurs: e.target.value })}
                  placeholder="Vers secteurs"
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* Œil gauche */}
          <div className="p-4 border rounded-lg space-y-4">
            <h4 className="font-medium text-primary">Œil Gauche</h4>
            
            <div className="space-y-2">
              <Label className="text-sm">Diamètre</Label>
              <RadioGroup
                value={data.pupille_g_diametre || ''}
                onValueChange={(value) => onChange({ pupille_g_diametre: value as any })}
                disabled={readOnly}
                className="flex gap-4"
              >
                {[
                  { value: 'mydriase', label: 'Mydriase' },
                  { value: 'ok', label: 'Ok' },
                  { value: 'myosis', label: 'Myosis' },
                ].map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <RadioGroupItem value={value} id={`diam-g-${value}`} />
                    <Label htmlFor={`diam-g-${value}`} className="text-sm cursor-pointer">{label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Bords</Label>
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pupille_g_bords_grignotes"
                    checked={data.pupille_g_bords_grignotes}
                    onCheckedChange={(checked) => handleCheckboxChange('pupille_g_bords_grignotes', !!checked)}
                    disabled={readOnly}
                  />
                  <Label htmlFor="pupille_g_bords_grignotes" className="text-sm cursor-pointer">Grignotés</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pupille_g_bords_cabosses"
                    checked={data.pupille_g_bords_cabosses}
                    onCheckedChange={(checked) => handleCheckboxChange('pupille_g_bords_cabosses', !!checked)}
                    disabled={readOnly}
                  />
                  <Label htmlFor="pupille_g_bords_cabosses" className="text-sm cursor-pointer">Cabossés</Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Méplats (secteurs 1-12)</Label>
              <Input
                value={data.pupille_g_meplats}
                onChange={(e) => onChange({ pupille_g_meplats: e.target.value })}
                placeholder="Ex: 3, 7, 11"
                disabled={readOnly}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Ovalisation</Label>
                <RadioGroup
                  value={data.pupille_g_ovalisation || ''}
                  onValueChange={(value) => onChange({ pupille_g_ovalisation: value as any })}
                  disabled={readOnly}
                  className="flex gap-3"
                >
                  {['plus', 'plus_plus', 'plus_plus_plus'].map((v) => (
                    <div key={v} className="flex items-center space-x-1">
                      <RadioGroupItem value={v} id={`oval-g-${v}`} />
                      <Label htmlFor={`oval-g-${v}`} className="text-sm cursor-pointer">
                        {v === 'plus' ? '+' : v === 'plus_plus' ? '++' : '+++'}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                <Input
                  value={data.pupille_g_ovalisation_secteurs}
                  onChange={(e) => onChange({ pupille_g_ovalisation_secteurs: e.target.value })}
                  placeholder="Secteurs (1/7, 2/8, 6/12)"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Décentrement</Label>
                <RadioGroup
                  value={data.pupille_g_decentrement || ''}
                  onValueChange={(value) => onChange({ pupille_g_decentrement: value as any })}
                  disabled={readOnly}
                  className="flex gap-3"
                >
                  {['plus', 'plus_plus', 'plus_plus_plus'].map((v) => (
                    <div key={v} className="flex items-center space-x-1">
                      <RadioGroupItem value={v} id={`dec-g-${v}`} />
                      <Label htmlFor={`dec-g-${v}`} className="text-sm cursor-pointer">
                        {v === 'plus' ? '+' : v === 'plus_plus' ? '++' : '+++'}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                <Input
                  value={data.pupille_g_decentrement_secteurs}
                  onChange={(e) => onChange({ pupille_g_decentrement_secteurs: e.target.value })}
                  placeholder="Vers secteurs"
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Intoxination */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            Intoxination
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Acide</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { key: 'acide_oxalique', label: 'Oxalique', desc: 'Jaune terne à rouille' },
                { key: 'acide_pyruvique', label: 'Pyruvique', desc: 'Orangé' },
                { key: 'acide_urique', label: 'Urique', desc: 'Flocon blanc' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-start space-x-2 p-2 border rounded-lg">
                  <Checkbox
                    id={key}
                    checked={data[key as keyof IridologyObservationsData] as boolean}
                    onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyObservationsData, !!checked)}
                    disabled={readOnly}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor={key} className="text-sm cursor-pointer font-medium">{label}</Label>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Anneau</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { key: 'anneau_electrolytique', label: 'Électrolytique', desc: 'Blanc laiteux' },
                { key: 'anneau_lipidique', label: 'Lipidique', desc: 'Cerclage sombre' },
                { key: 'anneau_gerontoxon', label: 'Gérontoxon', desc: 'Portion nacrée' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-start space-x-2 p-2 border rounded-lg">
                  <Checkbox
                    id={key}
                    checked={data[key as keyof IridologyObservationsData] as boolean}
                    onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyObservationsData, !!checked)}
                    disabled={readOnly}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor={key} className="text-sm cursor-pointer font-medium">{label}</Label>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Taches toxiniques</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: 'taches_psores', label: 'Psores', desc: 'Nettes posées' },
                { key: 'taches_cholesterol', label: 'Cholestérol', desc: 'Grains de cannelle' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-start space-x-2 p-2 border rounded-lg">
                  <Checkbox
                    id={key}
                    checked={data[key as keyof IridologyObservationsData] as boolean}
                    onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyObservationsData, !!checked)}
                    disabled={readOnly}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor={key} className="text-sm cursor-pointer font-medium">{label}</Label>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Taches résiduelles</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { key: 'taches_cicatrisation', label: 'Cicatrisation', desc: 'Sombres' },
                { key: 'taches_stress', label: 'Stress', desc: 'Noires' },
                { key: 'taches_hemorragique', label: 'Hémorragique', desc: 'Petite grappe rougeâtre' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-start space-x-2 p-2 border rounded-lg">
                  <Checkbox
                    id={key}
                    checked={data[key as keyof IridologyObservationsData] as boolean}
                    onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyObservationsData, !!checked)}
                    disabled={readOnly}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor={key} className="text-sm cursor-pointer font-medium">{label}</Label>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Signes iriens */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Signes iriens</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { key: 'radii_solari', label: 'Radii solari' },
              { key: 'arcs_cercle', label: 'Arcs de cercle' },
              { key: 'vacuoles', label: 'Vacuoles' },
              { key: 'lacunes', label: 'Lacunes' },
              { key: 'cryptes', label: 'Cryptes' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2 p-2 border rounded-lg">
                <Checkbox
                  id={key}
                  checked={data[key as keyof IridologyObservationsData] as boolean}
                  onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyObservationsData, !!checked)}
                  disabled={readOnly}
                />
                <Label htmlFor={key} className="text-sm cursor-pointer">{label}</Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Conclusions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Conclusions psycho-émotionnelles</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={data.conclusions_psy}
            onChange={(e) => onChange({ conclusions_psy: e.target.value })}
            placeholder="Notes et conclusions..."
            disabled={readOnly}
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Avertissement légal */}
      <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
        <p className="italic">
          <strong>Rappel :</strong> Ces observations iridologiques constituent une lecture descriptive du terrain
          et ne sauraient remplacer un diagnostic médical. L'iridologie est un outil d'observation complémentaire.
        </p>
      </div>
    </div>
  );
};
